import React, { Suspense } from 'react';
import { Link } from '../../router';
import SEOHead from '../SEOHead';
import TrustBar from '../TrustBar';
import ResponsiveImage from '../ResponsiveImage';
import DeferredRender from '../DeferredRender';
import './Home.css';
import config from '../../config'
import { buildNightlyOffer, getPriceRange } from '../../utils/pricing';
import { buildLodgingBusinessSchema } from '../../utils/schema';
import { getFirstImage } from '../../utils/galleryImages';
import { useTranslation } from 'react-i18next';

const HomeDeferredContent = React.lazy(() => import('./HomeDeferredContent'));
const apartmentSchemaNames = {
    garden: 'Garden Apartment',
    terrace: 'Terrace Apartment',
};

const Home = () => {
    const { t } = useTranslation(['home', 'common']);
    // Build comprehensive VacationRental schema from config
    const baseUrl = window.location.origin;
    const heroImage = getFirstImage(['upper-terrasse']);

    const vacationRentalSchema = {
        "@context": "https://schema.org",
        "@type": "VacationRental",
        "name": config.propertyName,
        "description": config.property.description,
        "url": baseUrl,
        "image": config.property.images.map(img => `${baseUrl}${img}`),
        "address": {
            "@type": "PostalAddress",
            "streetAddress": config.property.streetAddress,
            "addressLocality": config.property.addressLocality,
            "addressRegion": config.property.addressRegion,
            "postalCode": config.property.postalCode,
            "addressCountry": config.property.addressCountry
        },
        "geo": {
            "@type": "GeoCoordinates",
            "latitude": config.property.geo.latitude,
            "longitude": config.property.geo.longitude
        },
        "numberOfBedrooms": config.property.numberOfBedrooms,
        "numberOfBathroomsTotal": config.property.numberOfBathrooms,
        "occupancy": {
            "@type": "QuantitativeValue",
            "minValue": config.property.minOccupancy,
            "maxValue": config.property.maxOccupancy
        },
        "floorSize": {
            "@type": "QuantitativeValue",
            "value": config.property.floorSize.value,
            "unitCode": config.property.floorSize.unitCode
        },
        "amenityFeature": config.property.amenities.map(amenity => ({
            "@type": "LocationFeatureSpecification",
            "name": amenity
        })),
        "telephone": config.contact.phone,
        "email": config.contact.email,
        "potentialAction": {
            "@type": "ReserveAction",
            "target": {
                "@type": "EntryPoint",
                "urlTemplate": `${baseUrl}/contact`,
                "actionPlatform": [
                    "http://schema.org/DesktopWebPlatform",
                    "http://schema.org/MobileWebPlatform"
                ]
            },
            "result": {
                "@type": "Reservation",
                "name": "Book Casa das Escadinhas"
            }
        },
        "containsPlace": Object.values(config.apartments).map((apt) => {
            const offer = buildNightlyOffer(apt.id);
            return {
                "@type": "Accommodation",
                "name": apartmentSchemaNames[apt.id] || apt.slug,
                "url": `${baseUrl}/apartments/${apt.slug}`,
                "numberOfBedrooms": apt.bedrooms,
                "occupancy": {
                    "@type": "QuantitativeValue",
                    "minValue": 1,
                    "maxValue": apt.maxGuests
                },
                ...(offer ? { "offers": offer } : {})
            };
        })
    };

    // priceRange comes from config.pricing so structured data and visible
    // prices share one source. Omitted entirely when no prices are set.
    const priceRange = getPriceRange();
    if (priceRange) {
        vacationRentalSchema.priceRange = priceRange;
    }

    // LodgingBusiness reinforces the property as a local business entity (the
    // type Google ties to Maps/local results); it sits alongside VacationRental.
    const lodgingBusinessSchema = buildLodgingBusinessSchema(baseUrl);

    return (
        <div className="home">
            <SEOHead pageKey="home" schemaMarkup={[vacationRentalSchema, lodgingBusinessSchema]} />
            <div className="hero-container">
                <ResponsiveImage
                    image={heroImage}
                    src="/images/upper-terrasse/upper-terrasse-1.jpg"
                    alt={t('home:hero.imageAlt')}
                    sizes="100vw"
                    loading="eager"
                    fetchPriority="high"
                    className="hero-picture"
                    imgClassName="hero-image"
                />
                <div className="hero-content">
                    <p className="hero-kicker">{t('home:hero.kicker')}</p>
                    <h1>{t('home:hero.title', { propertyName: config.propertyName })}</h1>
                    <p>{t('home:hero.subtitle')}</p>
                    <div className="hero-buttons">
                        <Link to="/contact" className="btn-primary">{t('common:cta.requestToBook')}</Link>
                        <Link to="/gallery" className="btn-secondary">{t('common:buttons.viewGallery')}</Link>
                    </div>
                </div>
            </div>

            <TrustBar />

            <DeferredRender rootMargin="900px" idleTimeout={1600}>
                <Suspense fallback={null}>
                    <HomeDeferredContent />
                </Suspense>
            </DeferredRender>
        </div>
    );
};

export default Home;
